import React, { type CSSProperties, useState, type JSX } from "react";
import { twMerge } from "tailwind-merge";

import { SupText } from "../../../components/UI/Text";
import SupXSmall from "../../../icons/XSmall";

interface AlertBarProps {
  visible?: boolean;
  icon?: JSX.Element;
  title?: string;
  content?: string | JSX.Element;
  className?: string;
  color?: "info" | "warning" | "success" | "error";
  orientation?: "horizontal" | "vertical";
  closeable?: boolean;
  onClose?: () => void;
  style?: CSSProperties;
}

const ALERT_BAR_COLORS = {
  info: "bg-info-500 text-info-50",
  warning: "bg-warning-600 text-warning-50",
  success: "bg-success-700 text-success-50",
  error: "bg-error-400 text-error-975",
} as const;

export function SupAlertBar({
  visible: visibilityProp = true,
  icon,
  title,
  content,
  className,
  style,
  closeable = false,
  color = "info",
  orientation = "horizontal",
  onClose,
}: AlertBarProps) {
  const [visible, setVisibility] = useState(true);

  const handleClose = () => {
    onClose?.();
    setVisibility(false);
  };

  if (orientation === "vertical") {
    return visible && visibilityProp ? (
      <div className={twMerge("sup-alert-bar relative flex flex-col justify-center py-3 px-8 gap-2", ALERT_BAR_COLORS[color], className)} style={style}>
        {title && (
          <SupText as="div" variant="subtitle1" weight="bold" className="flex items-center justify-center gap-2">
            {icon}
            <b>{title}</b>
          </SupText>
        )}
        {content &&
          (typeof content === "string" ? (
            <SupText as="div" variant="body1" className="leading-normal text-center uppercase">
              {content}
            </SupText>
          ) : (
            content
          ))}

        {closeable && (
          <button className="absolute top-2 right-2" type="button" onClick={handleClose}>
            <SupXSmall />
          </button>
        )}
      </div>
    ) : null;
  }

  return visible && visibilityProp ? (
    <div className={twMerge("sup-alert-bar flex items-center justify-between px-2 py-4 gap-1.5 md:px-4 md:gap-4", ALERT_BAR_COLORS[color], className)} style={style}>
      {icon && <div className="inline-flex justify-center items-center shrink-0 w-5 md:w-10">{icon}</div>}
      <div className={twMerge("text-left", (!icon || !closeable) && "w-full")}>
        {title && (
          <SupText as="span" variant="subtitle1" weight="bold" className="mr-2">
            {title}
          </SupText>
        )}
        {content &&
          (typeof content === "string" ? (
            <SupText as="span" variant="body1" className="uppercase">
              {content}
            </SupText>
          ) : (
            content
          ))}
      </div>
      {closeable && (<div className="inline-flex justify-center items-center shrink-0 w-5 md:w-10">
          <button type="button" onClick={handleClose}>
            <SupXSmall />
          </button>
      </div>)}
    </div>
  ) : null;
}
